import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'aloha_ai_model';

const AIModelContext = createContext(null);

export const useAIModel = () => {
  const ctx = useContext(AIModelContext);
  // Safe fallback if a component mounts outside the provider
  if (!ctx) return { model: 'gemini', setModel: () => {}, models: [], grouped: {}, loading: false, default: 'gemini-2.5-flash', canSelect: false };
  return ctx;
};

export const AIModelProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [models, setModels] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [defaultModel, setDefaultModel] = useState('gemini-2.5-flash');
  const [model, setModelState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'gemini');
  const [loading, setLoading] = useState(true);
  const [canSelect, setCanSelect] = useState(false);

  const setModel = useCallback((m) => {
    setModelState(m);
    localStorage.setItem(STORAGE_KEY, m);
  }, []);

  const load = useCallback(async () => {
    if (!isAuthenticated) { setCanSelect(false); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ai/models`);
      setModels(data.models || []);
      setGrouped(data.grouped || {});
      setDefaultModel(data.default);
      const allowed = !!data.selection_allowed;
      setCanSelect(allowed);
      if (!allowed) { setModel(data.default); return; }   // members always use the default model
      // If current selection isn't available, snap to default
      const cur = localStorage.getItem(STORAGE_KEY) || 'gemini';
      const avail = (data.models || []).filter((m) => m.available).map((m) => m.id);
      const aliasOk = cur === 'gemini' && avail.includes('gemini-2.5-flash');
      if (!avail.includes(cur) && !aliasOk) {
        setModel(data.default);
      }
    } catch (e) {
      // keep default
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, setModel]);

  useEffect(() => { load(); }, [load]);

  return (
    <AIModelContext.Provider value={{ model, setModel, models, grouped, default: defaultModel, loading, canSelect, reload: load }}>
      {children}
    </AIModelContext.Provider>
  );
};

export default AIModelContext;
